// Settings — Team Members hooks (Settings-01).

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import {
  archiveBrokerLocation,
  confirmPasswordReset,
  connectMicrosoft365,
  createBrokerLocation,
  createProviderKey,
  fetchBeneAIAgent,
  fetchBrokerLocations,
  fetchBrokerSettings,
  fetchCredentialShapes,
  fetchProviderKeys,
  fetchTeamMembers,
  fetchTitleOptions,
  fetchUsStates,
  inviteTeamMember,
  patchBeneAIDefaultModel,
  patchBrokerSettings,
  requestPasswordChangeLink,
  resendInvitation,
  restoreBrokerLocation,
  revokeInvitation,
  setDefaultProviderKey,
  updateBrokerLocation,
  updateTeamMember,
} from "./api";
import type {
  BeneAIAgentSummary,
  BrokerLocation,
  BrokerLocationCreateInput,
  BrokerLocationListResponse,
  BrokerLocationPatchInput,
  BrokerSettings,
  CreateProviderKeyInput,
  CreateProviderKeyResponse,
  CredentialShapeListResponse,
  InviteTeamMemberInput,
  Microsoft365ConnectResponse,
  PasswordChangeLinkResponse,
  PasswordResetConfirmInput,
  PatchDefaultModelInput,
  ProviderKeyListResponse,
  ProviderKeyRow,
  TeamMember,
  TeamMemberListResponse,
  TeamMemberPatchInput,
  TitleOptionsResponse,
  UsStatesResponse,
} from "./types";


export const TEAM_MEMBERS_QUERY_KEY = "team-members" as const;


export function useTeamMembersQuery(): UseQueryResult<
  TeamMemberListResponse
> {
  return useQuery({
    queryKey: [TEAM_MEMBERS_QUERY_KEY, "list"],
    queryFn: ({ signal }) => fetchTeamMembers({ signal }),
  });
}


export function useTitleOptionsQuery(): UseQueryResult<TitleOptionsResponse> {
  return useQuery({
    queryKey: [TEAM_MEMBERS_QUERY_KEY, "titles"],
    queryFn: ({ signal }) => fetchTitleOptions({ signal }),
  });
}


export interface UpdateTeamMemberVariables {
  member_id: string;
  values: TeamMemberPatchInput;
}


export function useUpdateTeamMemberMutation(): UseMutationResult<
  TeamMember,
  Error,
  UpdateTeamMemberVariables
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: UpdateTeamMemberVariables) =>
      updateTeamMember(vars.member_id, vars.values),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [TEAM_MEMBERS_QUERY_KEY, "list"],
      });
      queryClient.invalidateQueries({
        queryKey: [TEAM_MEMBERS_QUERY_KEY, "titles"],
      });
    },
  });
}


export function useInviteTeamMemberMutation(): UseMutationResult<
  TeamMember,
  Error,
  InviteTeamMemberInput
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: InviteTeamMemberInput) => inviteTeamMember(input),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [TEAM_MEMBERS_QUERY_KEY, "list"],
      });
      queryClient.invalidateQueries({
        queryKey: [TEAM_MEMBERS_QUERY_KEY, "titles"],
      });
    },
  });
}


export function useResendInvitationMutation(): UseMutationResult<
  TeamMember,
  Error,
  string
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (invitationId: string) => resendInvitation(invitationId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [TEAM_MEMBERS_QUERY_KEY, "list"],
      });
    },
  });
}


export function useRevokeInvitationMutation(): UseMutationResult<
  void,
  Error,
  string
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (invitationId: string) => revokeInvitation(invitationId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [TEAM_MEMBERS_QUERY_KEY, "list"],
      });
    },
  });
}


// ── Settings-02 — Broker Locations ───────────────────────────────────────


export const BROKER_LOCATIONS_QUERY_KEY = "broker-locations" as const;


export function useBrokerLocationsQuery(opts?: {
  includeArchived?: boolean;
}): UseQueryResult<BrokerLocationListResponse> {
  const includeArchived = Boolean(opts?.includeArchived);
  return useQuery({
    queryKey: [BROKER_LOCATIONS_QUERY_KEY, "list", { includeArchived }],
    queryFn: ({ signal }) =>
      fetchBrokerLocations({ signal, includeArchived }),
  });
}


function invalidateLocations(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({
    queryKey: [BROKER_LOCATIONS_QUERY_KEY, "list"],
  });
}


export function useCreateBrokerLocationMutation(): UseMutationResult<
  BrokerLocation, Error, BrokerLocationCreateInput
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input) => createBrokerLocation(input),
    onSuccess: () => invalidateLocations(qc),
  });
}


export interface UpdateBrokerLocationVariables {
  location_id: string;
  values: BrokerLocationPatchInput;
}


export function useUpdateBrokerLocationMutation(): UseMutationResult<
  BrokerLocation, Error, UpdateBrokerLocationVariables
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars) => updateBrokerLocation(vars.location_id, vars.values),
    onSuccess: () => invalidateLocations(qc),
  });
}


export function useArchiveBrokerLocationMutation(): UseMutationResult<
  BrokerLocation, Error, string
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (locationId: string) => archiveBrokerLocation(locationId),
    onSuccess: () => invalidateLocations(qc),
  });
}


export function useRestoreBrokerLocationMutation(): UseMutationResult<
  BrokerLocation, Error, string
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (locationId: string) => restoreBrokerLocation(locationId),
    onSuccess: () => invalidateLocations(qc),
  });
}


// ── Settings-02 — Password change ────────────────────────────────────────


export function useRequestPasswordChangeLinkMutation(): UseMutationResult<
  PasswordChangeLinkResponse, Error, void
> {
  return useMutation({
    mutationFn: () => requestPasswordChangeLink(),
  });
}


export function useConfirmPasswordResetMutation(): UseMutationResult<
  { detail: string }, Error, PasswordResetConfirmInput
> {
  return useMutation({
    mutationFn: (input) => confirmPasswordReset(input),
  });
}


// ── Integrations — Microsoft 365 OAuth ───────────────────────────────────


export function useMicrosoft365ConnectMutation(): UseMutationResult<
  Microsoft365ConnectResponse, Error, void
> {
  return useMutation({
    mutationFn: () => connectMicrosoft365(),
  });
}


// ── Settings-02b — US states reference ───────────────────────────────────


export const US_STATES_QUERY_KEY = "us-states" as const;


/** Cached states list. The data is static (50 + DC + 5 territories)
 * so we set both ``staleTime`` and ``gcTime`` aggressively — the
 * AddLocation modal reads from cache after the first open. */
export function useUsStatesQuery(): UseQueryResult<UsStatesResponse> {
  return useQuery({
    queryKey: [US_STATES_QUERY_KEY],
    queryFn: ({ signal }) => fetchUsStates({ signal }),
    staleTime: 24 * 60 * 60 * 1000,
    gcTime: 24 * 60 * 60 * 1000,
  });
}


// ── Settings-04 — LLM Provider Keys ──────────────────────────────────────


export const LLM_QUERY_KEY = "llm-settings" as const;


export function useBrokerSettingsQuery(
  entityId: string | null,
): UseQueryResult<BrokerSettings> {
  return useQuery({
    queryKey: [LLM_QUERY_KEY, "broker-settings", entityId],
    enabled: Boolean(entityId),
    queryFn: ({ signal }) =>
      // Non-null assertion is fine — ``enabled`` gates the call.
      fetchBrokerSettings(entityId as string, { signal }),
  });
}


export function useProviderKeysQuery(): UseQueryResult<ProviderKeyListResponse> {
  return useQuery({
    queryKey: [LLM_QUERY_KEY, "provider-keys"],
    queryFn: ({ signal }) => fetchProviderKeys({ signal }),
  });
}


export function useBeneAIAgentQuery(): UseQueryResult<BeneAIAgentSummary> {
  return useQuery({
    queryKey: [LLM_QUERY_KEY, "beneai-agent"],
    queryFn: ({ signal }) => fetchBeneAIAgent({ signal }),
  });
}


export function usePatchBrokerSettingsMutation(
  entityId: string | null,
): UseMutationResult<BrokerSettings, Error, Partial<BrokerSettings>> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input) => {
      if (!entityId) {
        return Promise.reject(new Error("missing_entity_id"));
      }
      return patchBrokerSettings(entityId, input);
    },
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: [LLM_QUERY_KEY, "broker-settings", entityId],
      });
    },
  });
}


export function useSetDefaultProviderKeyMutation(): UseMutationResult<
  ProviderKeyRow, Error, string
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (providerKeyId) => setDefaultProviderKey(providerKeyId),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: [LLM_QUERY_KEY, "provider-keys"],
      });
    },
  });
}


export function usePatchDefaultModelMutation(): UseMutationResult<
  BeneAIAgentSummary, Error, PatchDefaultModelInput
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input) => patchBeneAIDefaultModel(input),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: [LLM_QUERY_KEY, "beneai-agent"],
      });
    },
  });
}


export function useCredentialShapesQuery(): UseQueryResult<
  CredentialShapeListResponse
> {
  return useQuery({
    queryKey: [LLM_QUERY_KEY, "credential-shapes"],
    queryFn: ({ signal }) => fetchCredentialShapes({ signal }),
    // The shape registry is static; one fetch per session is plenty.
    staleTime: 24 * 60 * 60 * 1000,
    gcTime: 24 * 60 * 60 * 1000,
  });
}


export function useCreateProviderKeyMutation(): UseMutationResult<
  CreateProviderKeyResponse, Error, CreateProviderKeyInput
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input) => createProviderKey(input),
    onSuccess: () => {
      // Refresh the list so the new row appears immediately.
      qc.invalidateQueries({
        queryKey: [LLM_QUERY_KEY, "provider-keys"],
      });
    },
  });
}
